import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, onMounted, onUnmounted, ref } from 'vue';
import { debounce } from 'lodash-es';
import { useRoute } from 'vue-router/composables';

import {
  SearchBar,
  LoaderIndicator,
  HelperViewStatus,
  DataTable,
  ButtonIconPrimary
} from '@warrenbrasil/nebraska-web';
import { FilterSelectButton } from '@/modules/common/components/FilterSelectButton';
import {
  EmptyHelper,
  MarketStatus,
  SidebarFilters
} from '@/modules/catalog/components';

import { registerObserver } from '@/modules/catalog/views/Products/helpers/registerObserver';
import { columns } from './columns';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import {
  IFixedIncomeProduct,
  IFixedIncomeFilter,
  IPagination,
  IProductsParams,
  IMarketStatus,
  getFixedIncomeInstruments,
  getFixedIncomeProductsV2
} from '@/modules/catalog/services/products';

interface Props {
  isBuilder: boolean;
}
interface EmitEvents {
  (event: 'select-product', id: string): string;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'FixedIncomeProducts',
  props: {
    isBuilder: { type: Boolean }
  },
  emits: ["select-product"],
  setup(__props: any, { emit }: { emit: ({
  (event: 'select-product', id: string): string;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  isBuilder: boolean;
};





const route = useRoute();
const products = ref<IFixedIncomeProduct[]>([]);
const filters = ref<IFixedIncomeFilter[]>([]);
const pagination = ref<IPagination>();
const marketStatus = ref<IMarketStatus>();

const params = ref<IProductsParams>({
  pageIndex: 1,
  pageSize: 10
});

const isLoading = ref(true);
const isScrolling = ref(false);
const isSorting = ref(false);
const hasError = ref(false);
const isSearchBarHover = ref(false);
const isSearchBarFocus = ref(false);

const searchValue = ref('');
const isFiltersOpen = ref(false);
const clearFilters = ref(false);
const filtersCount = ref(0);
const selectedFilters = ref<Record<string, string | string[]>>({});
const scrollTop = ref(document.documentElement.scrollTop);

const columnsTable = computed(() => {
  return columns(
    pagination.value!.totalRecords,
    marketStatus.value?.isMarketOpen || false,
    isEnabledSecondaryMarket.value
  );
});

const hasScrolled = computed(() => {
  return scrollTop.value >= 100;
});

const isEmpty = computed(() => {
  return products.value.length === 0;
});

const emptyDataType = computed(() => {
  if (searchValue.value) return 'search';

  if (filtersCount.value > 0) return 'filter';

  return 'empty';
});

const scrolledButtonClass = computed(() => {
  return props.isBuilder
    ? 'fixed-income-products__button builder'
    : 'fixed-income-products__button';
});

const isEnabledSecondaryMarket = computed(() => {
  return FeatureFlagsModule.isEnabled(FeatureFlags.ENABLE_SECONDARY_MARKET);
});

const onInputChange = debounce(handleSearch, 500);

function openFilters() {
  isFiltersOpen.value = true;
}

function closeFilters() {
  isFiltersOpen.value = false;
}

async function handleClear() {
  if (searchValue.value) {
    searchValue.value = '';

    handleSearch();
  } else clearFilters.value = true;
}

async function applyFilters(
  applyedFilters: IFixedIncomeFilter[],
  count: number
) {
  clearFilters.value = false;
  filtersCount.value = count;

  if (applyedFilters.length === 0) {
    selectedFilters.value = {};
  } else {
    selectedFilters.value = {};

    applyedFilters.forEach(filter => {
      // SidebarFilters now transforms maturityDate into minimumMaturity and maximumMaturity
      // So we just assign them directly
      selectedFilters.value[filter.value] = filter.selectedFilters;
    });
  }

  params.value = {
    ...params.value,
    pageIndex: 1,
    sortField: undefined,
    sortDirection: undefined
  };

  closeFilters();

  await fetchFixedIncomeProducts();
  registerObserver(fetchFixedIncomeProducts);
}

async function onSort(index: number, isSort: boolean, value: string) {
  const sortDirection =
    index === -1 ? undefined : isSort ? 'Ascending' : 'Descending';
  const sortField = index === -1 ? undefined : value;

  params.value = {
    ...params.value,
    pageIndex: 1,
    sortField,
    sortDirection
  };

  await fetchFixedIncomeProducts(false, false, true);
  registerObserver(fetchFixedIncomeProducts);
}

async function handleSearch() {
  params.value = {
    pageSize: 10,
    pageIndex: 1,
    searchTerm: searchValue.value
  };

  await fetchFixedIncomeProducts();
  registerObserver(fetchFixedIncomeProducts);
}

async function retry() {
  await fetchFixedIncomeProducts();
  registerObserver(fetchFixedIncomeProducts);
}

function goToTop() {
  document.documentElement.scrollTo(0, 0);
}

async function fetchFixedIncomeProducts(
  loading = true,
  scrolling = false,
  sorting = false
) {
  try {
    isLoading.value = loading;
    isScrolling.value = scrolling;
    isSorting.value = sorting;
    hasError.value = false;

    if (isScrolling.value) {
      if (!pagination.value?.nextPage) return;

      params.value = {
        ...params.value,
        pageIndex: pagination.value.nextPage
      };
    }

    const data = isEnabledSecondaryMarket.value
      ? await getFixedIncomeProductsV2(selectedFilters.value, params.value)
      : await getFixedIncomeInstruments(selectedFilters.value, params.value);

    filters.value = data.filters;
    pagination.value = data.pagination;
    if (!isEnabledSecondaryMarket.value)
      marketStatus.value = data?.marketStatus;

    const items = data.items.map(item => {
      return {
        ...item
      };
    });

    if (isScrolling.value) {
      products.value.push(...items);
      return;
    }

    products.value = items;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
    isScrolling.value = false;
    isSorting.value = false;
  }
}

function handleProductClick(product: IFixedIncomeProduct) {
  emit('select-product', product.productId);

  const routerName = props.isBuilder
    ? `catalog/builder/${
        route.params.portfolioId ? route.params.portfolioId + '/' : ''
      }fixed-income/${product.productId}`
    : `products-view/catalog/fixed-income/${product.productId}`;

  window.history.pushState({}, '', `/app/#/${routerName}`);
}

function handleScrollTop() {
  scrollTop.value = document.documentElement.scrollTop;
}

onMounted(async () => {
  await fetchFixedIncomeProducts();

  registerObserver(fetchFixedIncomeProducts);
});

onBeforeMount(() => {
  window.addEventListener('scroll', handleScrollTop);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScrollTop);
});

return { __sfc: true,props, emit, route, products, filters, pagination, marketStatus, params, isLoading, isScrolling, isSorting, hasError, isSearchBarHover, isSearchBarFocus, searchValue, isFiltersOpen, clearFilters, filtersCount, selectedFilters, scrollTop, columnsTable, hasScrolled, isEmpty, emptyDataType, scrolledButtonClass, isEnabledSecondaryMarket, onInputChange, openFilters, closeFilters, handleClear, applyFilters, onSort, handleSearch, retry, goToTop, fetchFixedIncomeProducts, handleProductClick, handleScrollTop, SearchBar, LoaderIndicator, HelperViewStatus, DataTable, ButtonIconPrimary, FilterSelectButton, EmptyHelper, MarketStatus, SidebarFilters }
}

})